import { Track } from '../../shared/models/tracks-sfx/track.model';
import { Injectable } from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { MediaUpdate } from '../models/media-update.model';
import { PlayerStatus } from '../models/player-status.model';
import { Howl, Howler } from '../howler.js';
import { ErrorsService } from 'src/app/shared/services/errors.service';
import { RandomTrackService } from './random-track.service';
import { TrackQueueService } from './track-queue.service';

@Injectable({
  providedIn: 'root'
})
export class MediaService {

  private howl?: Howl = null;
  private volume: number = 1;

  public onTrackChange: ReplaySubject<Track> = new ReplaySubject(1);
  public onUpdate: ReplaySubject<MediaUpdate> = new ReplaySubject<MediaUpdate>(1);

  public track: Track;
  private hasError = false;
  private manualLoading = false;
  public duration = 0;
  public currentTime = 0;
  public currentSeek = 0;

  constructor(
    private errorsService: ErrorsService,
    private queueService: TrackQueueService,
    private randomService: RandomTrackService
  ) {
    Howler.html5PoolSize = 1;
  }

  public play(track: Track, seek: number = 0, addToQueue: boolean = true) {
    this.clearPlaying();

    if (!track) {
      return;
    }

    this.track = track;

    seek = this.getClampedSeek(seek);
    this.currentSeek = seek;
    // if (Howler.usingWebAudio) {
    //   console.log('Using webAudio: ' + Howler.usingWebAudio);
    //   Howler.ctx.
    // }
    Howler?.stop();
    Howler?.unload();
    this.howl = new Howl({
      src: [track.mp3],
      html5: true, // Force to HTML5 so that the audio can stream in (best for large files).
      volume: this.volume,
      pool: 1,
      onplay: () => {
        this.duration = this.howl.duration();
        this.howl.seek(this.currentSeek * this.duration);
        this.startPeriodicUpdate();
      },
      onload: () => {
        this.duration = this.howl.duration();
        this.howl.seek(seek * this.duration);
      },
      onend: () => {
        if (track.isSoundEffect) {
          this.stop();
          this.track = null;
          this.onTrackChange.next(null);
        } else {
          this.next();
        }
      },
      onpause: () => {
        this.stopPeriodicUpdate();
      },
      onstop: () => {
        this.stopPeriodicUpdate();
      },
      onseek: () => {
        this.sendUpdate();
      },
      loaderror: (id, err) => {
        this.errorsService.track('MediaService error: loaderror. Track (' + track.id + '): ' + track.title, ['MediaService', 'LoadError']);
        this.hasError = true;
        this.sendUpdate();
        this.stop();
      },
      playerror: (id, err) => {
        this.errorsService.track('MediaService error: playerror. Track (' + track.id + '): ' + track.title, ['MediaService', 'PlayError']);
        this.hasError = true;
        this.stop();
      }

    });
    this.howl?.play();

    this.onTrackChange.next(track);
    this.onUpdate.next({
      duration: this.duration,
      currentTime: this.currentTime,
      seek: this.currentSeek,
      status: this.getStatus()
    });
    if (addToQueue) {
      this.queueService.add(track);
    }
  }

  public resume() {
    if (this.hasError) {
      this.play(this.track);
    } else {
      this.howl?.play();
    }
  }

  public pause() {
    this.howl?.pause();
  }

  public stop() {
    this.clearPlaying();
  }

  public seekTo(seek: number) {
    this.currentSeek = seek;
    this.currentTime = seek * this.duration;
    this.howl?.seek(seek * this.duration);
    this.onUpdate.next({
      duration: this.duration,
      currentTime: this.currentTime,
      seek: seek,
      status: this.getStatus()
    });
  }

  public previous() {
    if (this.queueService.hasPrev) {
      this.play(this.queueService.movePrev(), 0, false);
    }
  }

  public next() {
    this.stop();
    if (this.queueService.hasNext) {
      this.play(this.queueService.moveNext(), 0, false);
    } else {
      this.manualLoading = true;
      this.randomService.getTrack().subscribe(track => {
        this.manualLoading = false;
        this.play(track);
      });
    }
  }

  public setVolume(volume: number) {
    this.volume = volume;
    this.howl?.volume(volume);
  }

  public isTrackActive(track: Track) {
    if (!track) {
      return false;
    }
    return this.track && track && this.track.id && track.id && this.track.id == track.id;
  }

  public isTrackPlaying(track: Track) {
    return this.isTrackActive(track) && this.isPlaying();
  }

  public isPlaying() {
    return this.howl?.playing();
  }

  public isPaused() {
    return !this.isPlaying();
  }

  public isLoading() {
    return this.howl?.state() === 'loading' || this.manualLoading;
  }

  public isError() {
    return this.hasError;
  }

  private periodicIntervalId = null;

  private startPeriodicUpdate() {
    this.periodicIntervalId = setInterval(() => {
      if (!this.howl) {
        this.stopPeriodicUpdate();
      } else {
        this.sendUpdate();
      }
    }, 200);
  }

  private stopPeriodicUpdate() {
    if (this.periodicIntervalId) {
      this.periodicIntervalId = null;
      clearInterval(this.periodicIntervalId);
      this.sendUpdate();
    }
  }

  private sendUpdate() {
    this.duration = this.howl?.duration();
    this.currentSeek = this.duration && this.howl ? this.getClampedSeek(this.howl.seek() / this.duration) : 0;
    this.currentTime = this.currentSeek * this.duration;
    this.onUpdate.next({
      duration: this.duration,
      currentTime: this.currentTime,
      seek: this.currentSeek,
      status: this.getStatus()
    });
  }

  private clearPlaying() {
    this.stopPeriodicUpdate();
    Howler.stop();
    // Howler.unload();
    this.hasError = false;
    this.howl = null;
    this.duration = 0;
    this.currentTime = 0;
    this.currentSeek = 0;
    this.sendUpdate();
  }

  private getStatus() {
    if (this.hasError) {
      return PlayerStatus.Error;
    } else if (this.isLoading()) {
      return PlayerStatus.Loading;
    } else if (this.isPlaying()) {
      return PlayerStatus.Playing;
    } else {
      return PlayerStatus.Paused;
    }
  }

  private getClampedSeek(seek: number) {
    if (seek > 1) {
      return 1;
    } else if (seek < 0) {
      return 0;
    }
    return seek;
  }

}
